"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { en, zh } from "./translations";

export type Locale = "en" | "zh";

const STORAGE_KEY = "cc_lang";

const dictionaries: Record<Locale, Record<string, string>> = { en, zh };

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "zh") return saved;
  } catch {}
  return "en";
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => getInitialLocale());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    } catch {}
  }, [locale]);

  const setLocale = (next: Locale) => setLocaleState(next);

  const t = (key: string): string => {
    const dict = dictionaries[locale];
    if (dict && dict[key]) return dict[key];
    // Fallback to English
    const enDict = dictionaries.en;
    if (enDict && enDict[key]) return enDict[key];
    // Last resort: return the key itself
    return key;
  };

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return ctx;
}
